import i18n from '../../i18n';
import type { ReportColumnKind } from './types';

/**
 * Report value formatting — one place, so a figure reads identically in the table, the summary
 * strip, the filter chips and the export filename.
 *
 * Two rules are load-bearing and must not be "simplified":
 *
 * 1. **Latin digits in every language.** A payroll figure rendered in Arabic-Indic digits cannot be
 *    compared against the payslip PDF or the bank file, both of which use Latin digits. Every
 *    formatter therefore runs through `numberLocale()`, which pins `-u-nu-latn`.
 * 2. **Dates are parsed as LOCAL calendar dates.** `new Date('2026-09-02')` is parsed as UTC
 *    midnight, which renders as 1 September for any user west of Greenwich. `parseLocalDate` reads
 *    the y/m/d fields directly instead.
 *
 * The functions take an optional `locale`; when omitted they fall back to the live `i18n.language`.
 */

/** Rendered in place of a value the report does not have. Not the same as a zero. */
export const EMPTY_VALUE = '—';

const NBSP = ' ';
const EN_DASH = '–';

function activeLanguage(): string {
  try {
    return i18n?.language || 'en';
  } catch {
    return 'en';
  }
}

/** Locale tag with the Latin numbering system pinned. Safe to pass to `Intl.*`. */
export function numberLocale(locale?: string): string {
  const base = (locale || activeLanguage() || 'en').split('-u-')[0];
  return `${base}-u-nu-latn`;
}

function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/* ── money and numbers ────────────────────────────────────────────────────── */

/**
 * `12,500.00 AED`.
 *
 * The currency code is appended after a non-breaking space rather than run through
 * `Intl.NumberFormat`'s `style: 'currency'`, because a tenant's configured code is free text and
 * may not be a real ISO 4217 code, which `Intl` rejects.
 */
export function formatMoney(
  value: number | null | undefined,
  currency?: string,
  locale?: string,
): string {
  const n = toNumber(value);
  if (n === null) return EMPTY_VALUE;
  const text = new Intl.NumberFormat(numberLocale(locale), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
  const code = currency?.trim();
  return code ? `${text}${NBSP}${code}` : text;
}

export function formatInt(value: number | null | undefined, locale?: string): string {
  const n = toNumber(value);
  if (n === null) return EMPTY_VALUE;
  return new Intl.NumberFormat(numberLocale(locale), { maximumFractionDigits: 0 }).format(n);
}

export function formatDecimal(
  value: number | null | undefined,
  digits = 2,
  locale?: string,
): string {
  const n = toNumber(value);
  if (n === null) return EMPTY_VALUE;
  return new Intl.NumberFormat(numberLocale(locale), {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(n);
}

/**
 * `formatPercent(0.125)` gives `12.5%`.
 *
 * The input is a FRACTION, not a 0-100 number. That matches the `0.0%` Excel number format the
 * server-side export pipeline writes for a Percent column, which multiplies by 100 itself, so a
 * report feeding the same raw value to both surfaces shows the same figure in both.
 */
export function formatPercent(
  value: number | null | undefined,
  digits = 1,
  locale?: string,
): string {
  const n = toNumber(value);
  if (n === null) return EMPTY_VALUE;
  return new Intl.NumberFormat(numberLocale(locale), {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(n);
}

/* ── durations ────────────────────────────────────────────────────────────── */

/** `8h 30m`, `0h`, `30m`, `-1h 15m`. A missing value gives the em dash. */
export function formatHours(decimalHours: number | null | undefined): string {
  const n = toNumber(decimalHours);
  if (n === null) return EMPTY_VALUE;
  const sign = n < 0 ? '-' : '';
  const totalMinutes = Math.round(Math.abs(n) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) return `${sign}${hours}h`;
  if (hours === 0) return `${sign}${minutes}m`;
  return `${sign}${hours}h ${String(minutes).padStart(2, '0')}m`;
}

/** `45m`, `1h 05m`. Same renderer as `formatHours`, so the two never drift apart. */
export function formatMinutes(minutes: number | null | undefined): string {
  const n = toNumber(minutes);
  if (n === null) return EMPTY_VALUE;
  return formatHours(n / 60);
}

/**
 * A duration, whatever shape it arrived in, as DECIMAL HOURS. `null` when it cannot be read.
 *
 * 🔴 THIS EXISTS BECAUSE ONE REPORT RECEIVED THREE DIFFERENT SHAPES FOR THE SAME QUANTITY. The
 * Shifts report's durations arrive as `08:30:00` (the ordinary .NET `TimeSpan`), as `7.5` (already
 * decimal, from a caller that did the arithmetic), and as `1 days, 02:30:00` — the last being a
 * python-style rendering the OLD Shifts tab produced by splitting the TimeSpan on `.` and
 * re-joining it with the word "days", which then got read back as data. The tab used to branch on
 * `split('.').length` in FIVE places and drop the seconds; one parser is the fix.
 *
 * Accepted:
 *   `8`, `7.5`, `-1.25`              decimal hours
 *   `08:30:00`, `8:30`, `-08:30:00`  clock-shaped
 *   `1.02:30:00`                     .NET days-prefixed TimeSpan
 *   `1 days, 02:30:00`, `1 day 02:30` the python-style / legacy-display shape
 * Anything else is `null` — never `NaN`, which renders as a figure and lies.
 */
export function parseHours(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const raw = value.trim();
  if (raw === '') return null;

  // `1 days, 02:30:00` / `1 day 02:30:00` — pull the day count out, then parse what is left.
  let days = 0;
  let rest = raw;
  const dayWord = /^(-)?\s*(\d+)\s*(?:days?|d)\b[\s,]*/i.exec(rest);
  let negative = false;
  if (dayWord) {
    negative = dayWord[1] === '-';
    days = Number(dayWord[2]);
    rest = rest.slice(dayWord[0].length).trim();
    if (rest === '') return negative ? -days * 24 : days * 24;
  }

  const match = /^(-)?(?:(\d+)\.)?(\d{1,4}):(\d{2})(?::(\d{2})(?:[.,](\d+))?)?$/.exec(rest);
  if (match) {
    const [, sign, tsDays, hours, minutes, seconds, fraction] = match;
    negative = negative || sign === '-';
    const fractionPart = fraction ? Number(`0.${fraction}`) : 0;
    const total =
      (days + Number(tsDays ?? 0)) * 24 +
      Number(hours) +
      Number(minutes) / 60 +
      (Number(seconds ?? 0) + fractionPart) / 3600;
    if (!Number.isFinite(total)) return null;
    return negative ? -total : total;
  }

  // Plain decimal hours. Anchored, so `3 shifts` does not read as 3 hours.
  if (/^-?\d+(\.\d+)?$/.test(rest)) {
    const n = Number(rest);
    if (!Number.isFinite(n)) return null;
    const total = days * 24 + Math.abs(n);
    return negative || n < 0 ? -total : total;
  }

  return null;
}

/**
 * Parses any of the duration shapes `parseHours` accepts and renders it through `formatHours`.
 * Anything unparseable returns the em dash rather than `NaNh`.
 */
export function formatHoursFromTimeSpan(ts: string | number | null | undefined): string {
  const hours = parseHours(ts);
  return hours === null ? EMPTY_VALUE : formatHours(hours);
}

/** `3 (45m)`. A zero count is just `0`, and a count with no minutes drops the parenthesis. */
export function formatCountWithMinutes(
  count: number | null | undefined,
  minutes: number | null | undefined,
  locale?: string,
): string {
  const c = toNumber(count) ?? 0;
  if (c === 0) return formatInt(0, locale);
  const m = toNumber(minutes) ?? 0;
  if (m === 0) return formatInt(c, locale);
  return `${formatInt(c, locale)} (${formatMinutes(m)})`;
}

/* ── dates ────────────────────────────────────────────────────────────────── */

/** Reads `yyyy-MM[-dd]` as a LOCAL calendar date. Never `new Date(iso)` for a date-only string. */
export function parseLocalDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, match[3] ? Number(match[3]) : 1);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

type DateFieldName = 'weekday' | 'day' | 'month' | 'year';

function dateFields(
  date: Date,
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions,
): Partial<Record<DateFieldName, string>> {
  const parts = new Intl.DateTimeFormat(numberLocale(locale), options).formatToParts(date);
  const out: Partial<Record<DateFieldName, string>> = {};
  for (const part of parts) {
    if (
      part.type === 'weekday' ||
      part.type === 'day' ||
      part.type === 'month' ||
      part.type === 'year'
    ) {
      out[part.type] = part.value;
    }
  }
  return out;
}

/**
 * `Tue 2 Sep 2026`.
 *
 * Assembled from `formatToParts` rather than a raw `format()` so the field ORDER is the report's
 * (weekday, day, month, year) in every language while the field NAMES stay localized. `en-US`
 * would otherwise emit `Tue, Sep 2, 2026`, which reads differently column to column.
 */
export function formatDay(dateIso: string | Date | null | undefined, locale?: string): string {
  const date = parseLocalDate(dateIso);
  if (!date) return EMPTY_VALUE;
  const f = dateFields(date, locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return [f.weekday, f.day, f.month, f.year].filter(Boolean).join(' ');
}

/** `Tue 2 Sep` — for a table already grouped by month, where the year is stated once above. */
export function formatDayShort(dateIso: string | Date | null | undefined, locale?: string): string {
  const date = parseLocalDate(dateIso);
  if (!date) return EMPTY_VALUE;
  const f = dateFields(date, locale, { weekday: 'short', day: 'numeric', month: 'short' });
  return [f.weekday, f.day, f.month].filter(Boolean).join(' ');
}

/** `Aug 2026` from `2026-08` (or from any parseable date). */
export function formatMonth(monthIso: string | Date | null | undefined, locale?: string): string {
  const date = parseLocalDate(monthIso);
  if (!date) return EMPTY_VALUE;
  const f = dateFields(date, locale, { month: 'short', year: 'numeric' });
  return [f.month, f.year].filter(Boolean).join(' ');
}

function isMonthKey(value: string): boolean {
  return /^\d{4}-\d{2}$/.test(value);
}

/**
 * The one-line period the reader sees on the filter chip, the empty state and the export header.
 *
 * Month inputs (`2026-08`) collapse to `Aug 2026` or `Aug – Oct 2026`; day inputs (`2026-08-01`)
 * collapse to `1–31 Aug 2026` when they share a month.
 */
export function formatPeriod(
  fromIso: string | null | undefined,
  toIso: string | null | undefined,
  locale?: string,
): string {
  const from = parseLocalDate(fromIso);
  const to = parseLocalDate(toIso);
  if (!from && !to) return EMPTY_VALUE;
  if (!from || !to) return formatDay(from ?? to, locale);

  const monthMode = isMonthKey(String(fromIso)) && isMonthKey(String(toIso));
  const sameYear = from.getFullYear() === to.getFullYear();
  const sameMonth = sameYear && from.getMonth() === to.getMonth();

  if (monthMode) {
    if (sameMonth) return formatMonth(from, locale);
    if (sameYear) {
      const a = dateFields(from, locale, { month: 'short' });
      const b = dateFields(to, locale, { month: 'short', year: 'numeric' });
      return `${a.month} ${EN_DASH} ${b.month} ${b.year}`;
    }
    return `${formatMonth(from, locale)} ${EN_DASH} ${formatMonth(to, locale)}`;
  }

  const dayOnly: Intl.DateTimeFormatOptions = { day: 'numeric' };
  const dayMonth: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const dayMonthYear: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };

  if (sameMonth && from.getDate() === to.getDate()) {
    const f = dateFields(from, locale, dayMonthYear);
    return [f.day, f.month, f.year].filter(Boolean).join(' ');
  }
  if (sameMonth) {
    const a = dateFields(from, locale, dayOnly);
    const b = dateFields(to, locale, dayMonthYear);
    return `${a.day}${EN_DASH}${b.day} ${b.month} ${b.year}`;
  }
  if (sameYear) {
    const a = dateFields(from, locale, dayMonth);
    const b = dateFields(to, locale, dayMonthYear);
    return `${a.day} ${a.month} ${EN_DASH} ${b.day} ${b.month} ${b.year}`;
  }
  const a = dateFields(from, locale, dayMonthYear);
  const b = dateFields(to, locale, dayMonthYear);
  return `${a.day} ${a.month} ${a.year} ${EN_DASH} ${b.day} ${b.month} ${b.year}`;
}

/* ── column-kind dispatch ─────────────────────────────────────────────────── */

export interface FormatByKindOptions {
  currency?: string;
  locale?: string;
  /** Decimal places for `decimal` (default 2) and `percent` (default 1). */
  digits?: number;
}

/**
 * Formats a raw column value for its kind. A missing TEXT value renders as nothing; a missing
 * NUMBER renders as an em dash, because a blank numeric cell reads as a layout bug.
 */
export function formatByKind(
  kind: ReportColumnKind,
  value: string | number | null | undefined,
  options: FormatByKindOptions = {},
): string {
  const { currency, locale, digits } = options;
  if (value === null || value === undefined || value === '') {
    return kind === 'text' || kind === 'status' || kind === 'custom' ? '' : EMPTY_VALUE;
  }
  switch (kind) {
    case 'money':
      return formatMoney(toNumber(value), currency, locale);
    case 'int':
      return formatInt(toNumber(value), locale);
    case 'decimal':
      return formatDecimal(toNumber(value), digits ?? 2, locale);
    case 'hours':
      return formatHours(toNumber(value));
    case 'minutes':
      return formatMinutes(toNumber(value));
    case 'percent':
      return formatPercent(toNumber(value), digits ?? 1, locale);
    case 'date':
      return formatDay(String(value), locale);
    case 'month':
      return formatMonth(String(value), locale);
    default:
      return String(value);
  }
}
